import React from "react";
import { useIntl } from "react-intl";
import { CustomInputLabel } from "src/app/components/CustomInput/CustomInput";
import { CustomTokenInput } from "src/app/components/CustomTokenInput/CustomTokenInput";
import { DateRangeTwoSelectors } from "src/app/components/DateRangeTwoSelectors/DateRangeTwoSelectors";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { SelectTags } from "src/app/components/SelectTags/SelectTags";
import { translateMessage } from "src/app/methods/translateMessage";
import CriteriaOptionSelector from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/AudiencesSearch/components/filters/CriteriaOptionSelector/CriteriaOptionSelector";
import { SetObjectState } from "src/hooks/useObjectState";
import { CCMonitorState, SOCIAL_LABELS } from "src/types";
import { subYears } from "date-fns";

interface Props {
  monitorData: CCMonitorState;
  setMonitorData: SetObjectState<CCMonitorState>;
}

const METRICS_LABELS: Record<string, string> = {
  "avg-er": "Avg. ER",
  "avg-views": "Avg. Views",
  "avg-likes": "Avg. Likes",
  "avg-comments": "Avg. Comments",
  "first-publication-date": "First Publication Date",
  "last-publication-date": "Last Publication Date",
  "keywords": "Keywords",
};

const METRICS_FEED = [
  "avg-er",
  "avg-likes",
  "avg-views",
  "avg-comments",
  "first-publication-date",
  "last-publication-date",
  "keywords",
];

const SOCIAL_FEED = ["instagram", "tiktok", "youtube"];

export const CCMGeneral = (props: Props) => {
  const { monitorData, setMonitorData } = props;

  const {
    socialPlatforms,
    startDate,
    endDate,
    keywords,
    keywordsMatchType,
    metrics,
  } = monitorData;

  const intl = useIntl();

  const platformClick = (platform: string) => {
    if (socialPlatforms.indexOf(platform) > -1) {
      setMonitorData({
        socialPlatforms: socialPlatforms.filter(
          (itemName) => itemName !== platform,
        ),
      });
    } else {
      setMonitorData({ socialPlatforms: [...socialPlatforms, platform] });
    }
  };

  const metricClick = (metric: string) => {
    if (metrics.indexOf(metric) > -1) {
      setMonitorData({
        metrics: metrics.filter((itemName) => itemName !== metric),
      });
    } else {
      setMonitorData({ metrics: [...metrics, metric] });
    }
  };

  return (
    <div>
      <p className="creator-content-monitoring__description">
        <IDHFormattedMessage
          id="ws_creator_content_monitor_general_description"
          defaultMessage="Feature that allows you to scan to check if there are publications with specific keywords (including tags and hashtags) on the accounts belonging to the Creators included in the campaign."
        />
      </p>

      <DateRangeTwoSelectors
        dateFrom={startDate}
        setDateFrom={(date) =>
          setMonitorData({ startDate: typeof date === "string" ? date : null })
        }
        dateTo={endDate}
        setDateTo={(date) =>
          setMonitorData({ endDate: typeof date === "string" ? date : null })
        }
        minDateFrom={subYears(new Date(), 1)}
      />

      <CustomInputLabel>
        <IDHFormattedMessage
          id="ws_social_platform"
          defaultMessage="Social Platform"
        />
      </CustomInputLabel>
      <SelectTags
        // @ts-ignore
        feed={SOCIAL_FEED}
        // @ts-ignore
        values={socialPlatforms}
        // @ts-ignore
        getCustomLabel={(value) => SOCIAL_LABELS[value]}
        setValue={platformClick}
        unsetValue={platformClick}
        clearValues={() => setMonitorData({ socialPlatforms: [] })}
      />

      <CustomInputLabel htmlFor="tags">
        <IDHFormattedMessage id="ws_keywords" defaultMessage="Keywords" />
      </CustomInputLabel>
      <div className="creator-content-monitoring__keywords-wrapper">
        <CustomTokenInput
          value={keywords}
          onChange={(newValue: string[]) =>
            setMonitorData({ keywords: newValue })
          }
          placeholder={translateMessage({
            intl,
            id: "ws_enter_keywords_to_detect",
            defaultMessage: "Enter keywords to detect",
          })}
          separators={[","]}
        />
        <CriteriaOptionSelector
          value={keywordsMatchType?.value}
          setValue={(newValue: { value: string }) =>
            setMonitorData({ keywordsMatchType: newValue })
          }
        />
      </div>

      <CustomInputLabel>
        <IDHFormattedMessage id="ws_metrics" defaultMessage="Metrics" />
      </CustomInputLabel>
      <SelectTags
        // @ts-ignore
        feed={METRICS_FEED}
        // @ts-ignore
        values={metrics}
        // @ts-ignore
        getCustomLabel={(value) => METRICS_LABELS[value]}
        setValue={metricClick}
        unsetValue={metricClick}
        clearValues={() => setMonitorData({ metrics: [] })}
      />

      {/* <SettingsRowElement
        titleId="ws_notification_icon"
        titleDefaultMessage="Notification icon"
        descId="ws_notification_icon_description"
        descDefaultMessage="Displays a notification icon in the extension field on the table alongside the number of detected publications."
        reversed
      >
        <CustomSwitch
          checked={notificationIcon}
          onChange={() =>
            setMonitorData({ notificationIcon: !notificationIcon })
          }
        />
      </SettingsRowElement> */}
    </div>
  );
};
